import { createElement, useState, type ReactNode } from "react";
import type { OverviewMetrics, StatsDailyResponse, StatsOverviewResponse, StatsRange } from "@townsy/contracts";
import { errorMessage } from "../api/client";
import { useStatsOverviewQuery } from "../api/queries";
import { useStatsDailyQuery } from "../api/stats";
import { formatCount, formatMetric, formatUtcDateTime } from "../lib/format";
import { DailyBars, utcDayLabel, type DailyBar } from "./stats/DailyBars";
import { StatIcon, type StatIconName } from "./stats/StatIcon";

/** The view picker: four ranges as cards, or every range (and every day) as tables. */
export type StatsView = "today" | "last7d" | "last30d" | "lifetime" | "all";
const VIEWS: Array<{ key: StatsView; label: string }> = [
  { key: "today", label: "Today" },
  { key: "last7d", label: "7 days" },
  { key: "last30d", label: "30 days" },
  { key: "lifetime", label: "Lifetime" },
  { key: "all", label: "All ranges" },
];

const TABLE_RANGES: Array<{ key: StatsRange; label: string }> = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7d", label: "Last 7 days" },
  { key: "last30d", label: "Last 30 days" },
  { key: "lifetime", label: "Lifetime" },
];

/** Metric groups, each with its own accent (stats.css): people, signs, visits, ordinary clicks, rewarded clicks. */
export type StatGroup = "people" | "signs" | "visits" | "clicks" | "rewarded";

export interface MetricDef {
  key: keyof OverviewMetrics;
  /** Card label (short). */
  label: string;
  /** Full name (tables, "How we count"). */
  name: string;
  /** Column heading in the daily table. */
  short: string;
  group: StatGroup;
  icon: StatIconName;
  hint: string;
  /** Unit word for a day's tooltip ("3 sessions"). */
  noun: (n: number) => string;
}

const plural = (one: string, many: string) => (n: number) => (n === 1 ? one : many);

export const METRICS: MetricDef[] = [
  {
    key: "townSessions",
    short: "Sessions",
    label: "Town sessions",
    name: "Town sessions",
    group: "people",
    icon: "sessions",
    hint: "New visitor sessions after 30 minutes of inactivity; room changes are not new visits.",
    noun: plural("session", "sessions"),
  },
  {
    key: "uniqueVisitors",
    short: "Visitors",
    label: "Unique visitors",
    name: "Estimated unique visitors",
    group: "people",
    icon: "visitors",
    hint: "Distinct pseudonymous identities in the period; not guaranteed unique people across devices.",
    noun: plural("distinct visitor that day", "distinct visitors that day"),
  },
  {
    key: "signImpressions",
    short: "Impressions",
    label: "Sign impressions",
    name: "Visible sign impressions",
    group: "signs",
    icon: "eye",
    hint: "Filtered estimate: a sign at least 50% visible for 1 s in a foreground tab, deduplicated per property, owner interval and visitor per 30 minutes.",
    noun: plural("impression", "impressions"),
  },
  {
    key: "panelViews",
    short: "Panel views",
    label: "Panel views",
    name: "Property panel views",
    group: "visits",
    icon: "panel",
    hint: "Intentional opening of a listing, deduplicated for rapid repeats.",
    noun: plural("panel view", "panel views"),
  },
  {
    key: "roomEntries",
    short: "Entries",
    label: "Room entries",
    name: "Room entries",
    group: "visits",
    icon: "door",
    hint: "Authorised transitions into an interior; reconnects are not entries.",
    noun: plural("room entry", "room entries"),
  },
  {
    key: "outboundClicksOrdinary",
    short: "Clicks",
    label: "Website clicks",
    name: "Website clicks (ordinary)",
    group: "clicks",
    icon: "cursor",
    hint: "Eligible intentional link use; not proof the destination loaded.",
    noun: plural("click", "clicks"),
  },
  {
    key: "outboundClicksRewarded",
    short: "Rewarded clicks",
    label: "Rewarded website clicks",
    name: "Website clicks (rewarded)",
    group: "rewarded",
    icon: "coin",
    hint: "The subset tied to a disclosed sponsored task that can earn coins.",
    noun: plural("rewarded click", "rewarded clicks"),
  },
  {
    // ADR-0031 (founder 2026-10-20): a daily total to spot anything unusual; display only, not a limit.
    key: "offerCoinsEarned",
    short: "Offer coins",
    label: "Coins earned from offers",
    name: "Coins earned from offers",
    group: "rewarded",
    icon: "coin",
    hint: "Coins residents earned for completed offers, counted from Townsy's coin ledger. A virtual reward with no cash value; a count, not a limit.",
    noun: plural("coin", "coins"),
  },
];
const ORDINARY = METRICS.filter((m) => m.group !== "rewarded");
const REWARDED = METRICS.filter((m) => m.group === "rewarded");

/** Why a dash: a measurement that does not exist for that range (docs/01 section 6: dashes with explanations). */
export function unavailableReason(key: keyof OverviewMetrics, range: StatsRange): string {
  if (key === "uniqueVisitors" && range === "lifetime") return "Not measured for Lifetime: raw activity is kept 30 days, so a distinct count over all time is not computed rather than guessed.";
  return "Not available for this range.";
}

const dateOf = (iso: string, withYear = false) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", ...(withYear ? { year: "numeric" as const } : {}), timeZone: "UTC" });

/** One line saying exactly which UTC days a range covers (ranges are half-open [from, to)). */
export function rangeCaption(range: StatsRange, r: { from: string; to: string } | undefined): string {
  if (!r) return "Reporting days are UTC.";
  const lastDay = new Date(new Date(r.to).getTime() - 86_400_000).toISOString();
  switch (range) {
    case "today":
      return `${dateOf(r.from, true)} so far · the current UTC day`;
    case "yesterday":
      return `${dateOf(r.from, true)} · one UTC day`;
    case "last7d":
      return `${dateOf(r.from)} – ${dateOf(lastDay, true)} · 7 UTC days, today included`;
    case "last30d":
      return `${dateOf(r.from)} – ${dateOf(lastDay, true)} · 30 UTC days, today included`;
    case "lifetime":
      return "Everything still retained, up to today (UTC) · raw activity is kept 30 days";
  }
}

/** The bars a card shows: the last 7 UTC days for Today / 7 days, all 30 for 30 days / Lifetime; the range's days stand out. */
export function trendBars(daily: StatsDailyResponse, key: keyof OverviewMetrics, range: StatsRange): DailyBar[] {
  const span = range === "last30d" || range === "lifetime" ? 30 : 7;
  const days = daily.days.slice(-span);
  return days.map((d, i) => {
    const fromEnd = days.length - 1 - i;
    const inRange = range === "today" ? fromEnd === 0 : range === "yesterday" ? fromEnd === 1 : true;
    return { day: d.day, value: d.metrics[key] ?? 0, partial: d.partial, inRange };
  });
}

/** "How we count": server notes whose first words name a metric become definitions with that metric's icon. */
const NOTE_TERMS: Array<{ term: string; icon: StatIconName; group?: StatGroup }> = [
  { term: "Online now", icon: "online", group: "people" },
  { term: "Town sessions", icon: "sessions", group: "people" },
  { term: "Estimated unique visitors", icon: "visitors", group: "people" },
  { term: "Visible sign impressions", icon: "eye", group: "signs" },
  { term: "Property panel views", icon: "panel", group: "visits" },
  { term: "Room entries", icon: "door", group: "visits" },
  { term: "Outbound website clicks", icon: "cursor", group: "clicks" },
  { term: "Rewarded outbound clicks", icon: "coin", group: "rewarded" },
  { term: "Coins earned from offers", icon: "coin", group: "rewarded" },
];
export function splitNote(note: string): { term: string | null; text: string; icon: StatIconName; group?: StatGroup } {
  for (const t of NOTE_TERMS) {
    if (note.startsWith(`${t.term}:`)) {
      const text = note.slice(t.term.length + 1).trim();
      return { term: t.term, text: text.charAt(0).toUpperCase() + text.slice(1), icon: t.icon, group: t.group };
    }
  }
  if (note.startsWith("Reporting days use UTC")) return { term: "UTC reporting days", text: note, icon: "calendar" };
  if (note.startsWith("Lifetime unique visitors")) return { term: "Lifetime unique visitors", text: note, icon: "visitors" };
  return { term: null, text: note, icon: "info" };
}

function Heading({ level, className, children, id }: { level: 2 | 3; className?: string; children: ReactNode; id?: string }) {
  return createElement(`h${level}`, { className, id }, children);
}

/** Honest public statistics (docs/00 R10, docs/01 section 6, docs/07 section 7): live presence, range cards with daily trends, tables, notes. */
export function StatsOverview({ active, headingLevel = 2 }: { active: boolean; headingLevel?: 2 | 3 }) {
  const query = useStatsOverviewQuery(active);
  const daily = useStatsDailyQuery(active);
  if (query.isPending) {
    return (
      <div className="stats stats-loading" aria-busy="true">
        <p className="muted">Loading statistics…</p>
      </div>
    );
  }
  if (query.error) {
    return (
      <div className="notice notice-danger">
        <p style={{ margin: 0 }}>
          <strong>Statistics unavailable.</strong> {errorMessage(query.error)}
        </p>
        <button type="button" className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => void query.refetch()}>
          Retry
        </button>
      </div>
    );
  }
  return <StatsOverviewView data={query.data} daily={daily.data ?? null} dailyState={daily.data ? "ready" : daily.error ? "error" : "loading"} headingLevel={headingLevel} />;
}

export interface StatsOverviewViewProps {
  data: StatsOverviewResponse;
  daily: StatsDailyResponse | null;
  dailyState: "loading" | "error" | "ready";
  headingLevel?: 2 | 3;
  initialView?: StatsView;
  /** "How we count" starts open (evidence captures only; collapsed by default). */
  notesOpen?: boolean;
}

export function StatsOverviewView({ data, daily, dailyState, headingLevel = 2, initialView = "today", notesOpen = false }: StatsOverviewViewProps) {
  const [view, setView] = useState<StatsView>(initialView);
  const range: StatsRange = view === "all" ? "today" : view;
  const metrics = data.ranges[range]?.metrics;
  const onlineUnavailable = data.online.method.startsWith("unavailable");
  const ordinaryClicks = metrics?.outboundClicksOrdinary ?? null;
  const rewardedClicks = metrics?.outboundClicksRewarded ?? null;

  const tile = (m: MetricDef) => {
    const value = metrics?.[m.key] ?? null;
    const bars = daily ? trendBars(daily, m.key, range) : [];
    return (
      <div key={m.key} className={`stat-tile stat-${m.group}`} data-testid={`stat-${m.key}`} title={m.hint}>
        <dt className="stat-label">
          <span className="stat-chip">
            <StatIcon name={m.icon} size={18} />
          </span>
          <span>{m.label}</span>
        </dt>
        <dd className={`stat-value${value === null ? " dash" : ""}`}>{formatMetric(value)}</dd>
        {value === null ? <dd className="stat-note">{unavailableReason(m.key, range)}</dd> : null}
        <dd className="stat-trend">
          {dailyState === "ready" && bars.length ? (
            <>
              <DailyBars bars={bars} noun={m.noun} summaryLabel={m.key === "uniqueVisitors" ? "Distinct visitors per day" : `Daily ${m.label.toLowerCase()}`} />
              <span className="stat-trend-axis" aria-hidden="true">
                <span>{utcDayLabel(bars[0]!.day, false)}</span>
                <span className={m.key === "uniqueVisitors" ? "stat-trend-mid keep" : "stat-trend-mid"}>{m.key === "uniqueVisitors" ? "distinct/day" : "daily"}</span>
                <span>Today</span>
              </span>
            </>
          ) : dailyState === "loading" ? (
            <span className="stat-trend-skeleton" aria-hidden="true" />
          ) : (
            <span className="stat-trend-off">Daily trend unavailable right now.</span>
          )}
        </dd>
      </div>
    );
  };

  return (
    <div className="stats" data-testid="stats-overview">
      {data.includesTestData ? (
        <div className="stats-banner" role="note">
          <StatIcon name="warning" size={16} />
          <span>Development fixtures may be included in these numbers. They are not production evidence.</span>
        </div>
      ) : null}

      <div className="stats-top">
        <section className={`stats-online${onlineUnavailable ? " off" : ""}`} aria-labelledby="stats-online-title" data-testid="stats-online">
          <div className="stats-online-head">
            <span className="stats-live" aria-hidden="true" />
            <Heading level={headingLevel} className="stats-online-title" id="stats-online-title">
              Online now
            </Heading>
          </div>
          <div className="stats-online-value">{onlineUnavailable ? "—" : formatCount(data.online.count)}</div>
          <p className="stats-online-text">
            {onlineUnavailable ? `Not measured right now (${data.online.method.replace(/^unavailable:\s*/, "")}).` : `${data.online.count === 1 ? "visitor" : "visitors"} in town right now`}
          </p>
          <p className="stats-online-asof">
            {onlineUnavailable ? "" : `Live connection within ${data.online.windowSeconds} s; several tabs count once. `}As of {formatUtcDateTime(data.online.asOf)}
          </p>
        </section>

        <div className="stats-rangebox">
          <div className="stats-segmented" role="group" aria-label="Time range">
            {VIEWS.map((v) => (
              <button key={v.key} type="button" aria-pressed={view === v.key} onClick={() => setView(v.key)} data-testid={`stats-view-${v.key}`}>
                {v.key === "all" ? <StatIcon name="table" size={15} /> : null}
                {v.label}
              </button>
            ))}
          </div>
          <p className="stats-range-caption">
            <StatIcon name="calendar" size={16} />
            <span>{view === "all" ? "Every range side by side, then each of the last 30 UTC days" : rangeCaption(range, data.ranges[range])}</span>
          </p>
          <p className="stats-range-note">
            Ordinary and rewarded website clicks are always counted apart, never blended into one number. A dash means a measurement is not available for that range.
          </p>
        </div>
      </div>

      {view !== "all" ? (
        <>
          <section className="stats-section" aria-labelledby="stats-ordinary-title">
            <div className="stats-section-head">
              <Heading level={headingLevel} className="stats-section-title" id="stats-ordinary-title">
                Ordinary activity
              </Heading>
              <span className="stats-section-sub">What visitors did on their own</span>
            </div>
            <dl className="stat-grid">{ORDINARY.map(tile)}</dl>
          </section>

          <section className="stats-section stats-rewarded" aria-labelledby="stats-rewarded-title">
            <div className="stats-section-head">
              <Heading level={headingLevel} className="stats-section-title" id="stats-rewarded-title">
                Rewarded activity
              </Heading>
              <span className="stats-section-sub">Sponsored tasks that can earn coins</span>
            </div>
            <div className="stats-rewarded-body">
              <dl className="stat-grid stat-grid-single">{REWARDED.map(tile)}</dl>
              <div className="stats-rewarded-text">
                <p>
                  A rewarded click comes from a disclosed sponsored task. It is reported separately because a coin reward changes why someone clicks.
                </p>
                <p data-testid="stats-offer-coins-note">
                  Coins earned from offers are counted from Townsy's coin ledger. They are a virtual reward with no cash value, shown so anything unusual stands out; they are not a limit.
                </p>
                {ordinaryClicks !== null && rewardedClicks !== null ? (
                  <p className="stats-click-total" data-testid="stats-click-total">
                    <span className="stats-click-total-label">All website clicks</span>
                    <strong>{formatCount(ordinaryClicks + rewardedClicks)}</strong>
                    <span className="stats-click-split">
                      <span className="stats-key stats-key-clicks" aria-hidden="true" />
                      {formatCount(ordinaryClicks)} ordinary
                      <span className="stats-key stats-key-rewarded" aria-hidden="true" />
                      {formatCount(rewardedClicks)} rewarded
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </section>
        </>
      ) : (
        <AllRanges data={data} daily={daily} dailyState={dailyState} level={headingLevel} />
      )}

      <p className="stats-meta">
        <StatIcon name="clock" size={15} />
        <span>
          Reporting days are UTC. Updated {formatUtcDateTime(data.generatedAt)} (refreshes every 30 s while open) · definitions v{data.definitionsVersion}
          {data.lastAggregatedAt ? ` · last aggregation ${formatUtcDateTime(data.lastAggregatedAt)}` : " · no rollup yet"}.
        </span>
      </p>

      <details className="stats-how" open={notesOpen || undefined}>
        <summary>
          <StatIcon name="info" size={18} />
          <span>How we count</span>
        </summary>
        <dl className="stats-how-list">
          {data.notes.map((n, i) => {
            const note = splitNote(n);
            return (
              <div key={i} className={`stats-how-item${note.group ? ` stat-${note.group}` : " general"}`}>
                <dt>
                  <span className="stat-chip">
                    <StatIcon name={note.icon} size={16} />
                  </span>
                  <span>{note.term ?? "Good to know"}</span>
                </dt>
                <dd>{note.text}</dd>
              </div>
            );
          })}
          {daily ? (
            <div className="stats-how-item general">
              <dt>
                <span className="stat-chip">
                  <StatIcon name="calendar" size={16} />
                </span>
                <span>Daily bars</span>
              </dt>
              <dd>{daily.method}</dd>
            </div>
          ) : null}
          <div className="stats-how-item general">
            <dt>
              <span className="stat-chip">
                <StatIcon name="info" size={16} />
              </span>
              <span>Ordinary and rewarded</span>
            </dt>
            <dd>Ordinary and rewarded website clicks are reported separately and never blended into one “quality traffic” number.</dd>
          </div>
        </dl>
      </details>
    </div>
  );
}

/** "All ranges": every range in one table, then each day of the last 30 (the same numbers the bars draw). */
function AllRanges({ data, daily, dailyState, level }: { data: StatsOverviewResponse; daily: StatsDailyResponse | null; dailyState: "loading" | "error" | "ready"; level: 2 | 3 }) {
  const days = daily ? [...daily.days].reverse() : [];
  return (
    <section className="stats-section" aria-labelledby="stats-all-title" data-testid="stats-all-ranges">
      <div className="stats-section-head">
        <Heading level={level} className="stats-section-title" id="stats-all-title">
          All ranges
        </Heading>
        <span className="stats-section-sub">UTC days · dashes are explained in “How we count”</span>
      </div>
      <div className="stats-table-wrap">
        <table className="table stats-table">
          <caption className="visually-hidden">Town metrics by range (UTC days)</caption>
          <thead>
            <tr>
              <th scope="col">Metric</th>
              {TABLE_RANGES.map((r) => (
                <th key={r.key} scope="col" className="num">
                  {r.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {METRICS.map((m) => (
              <tr key={m.key} className={`stat-${m.group}${m.group === "rewarded" ? " stats-row-rewarded" : ""}`}>
                <th scope="row" title={m.hint}>
                  <span className="stats-key" aria-hidden="true" />
                  {m.name}
                </th>
                {TABLE_RANGES.map((r) => {
                  const v = formatMetric(data.ranges[r.key]?.metrics[m.key]);
                  return (
                    <td key={r.key} className={`num${v === "—" ? " dash" : ""}`} title={v === "—" ? unavailableReason(m.key, r.key) : undefined}>
                      {v}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="stats-section-head">
        <Heading level={level} className="stats-section-title">
          Each day
        </Heading>
        <span className="stats-section-sub">Newest first · unique visitors are distinct within each day</span>
      </div>
      {dailyState === "ready" ? (
        <div className="stats-table-wrap stats-table-tall">
          <table className="table stats-table" data-testid="stats-daily-table">
            <caption className="visually-hidden">Town metrics for each of the last 30 UTC days</caption>
            <thead>
              <tr>
                <th scope="col">UTC day</th>
                {METRICS.map((m) => (
                  <th key={m.key} scope="col" className={`num stat-${m.group}${m.group === "rewarded" ? " stats-col-rewarded" : ""}`} title={m.name}>
                    <span className="stats-key" aria-hidden="true" />
                    {m.short}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.day}>
                  <th scope="row" className="nowrap">
                    {utcDayLabel(d.day)}
                    {d.partial ? <span className="faint small"> · so far</span> : null}
                  </th>
                  {METRICS.map((m) => (
                    <td key={m.key} className={`num${m.group === "rewarded" ? " stats-col-rewarded" : ""}`}>
                      {formatMetric(d.metrics[m.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted small">{dailyState === "loading" ? "Loading the daily numbers…" : "The daily numbers are unavailable right now."}</p>
      )}
    </section>
  );
}
